import React from 'react';
import { set } from 'date-fns';
import { Calendar as CalendarIcon, Clock, X, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { CalendarPanel } from './CalendarPanel';
import { TimePanel } from './TimePanel';
import {
  parseWire, formatWire, formatDisplay, formatSummary, placeholderFor,
  isRange, hasTime, hasDate, mergeDateAndTime,
} from './dateUtils';

function useIsMobile(bp = 639) {
  const [m, setM] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width:${bp}px)`).matches
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width:${bp}px)`);
    const h = (e) => setM(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [bp]);
  return m;
}

export function TrinitiDateTimeCore(props) {
  const {
    mode = 'datetime', value, startValue, endValue, onChange,
    minDate, maxDate, disabledDates, minuteStep = 5,
    required = false, disabled = false, allowClear = false, error,
    placeholder, className, id, testId = 'tdt', label,
    startLabel = 'Start', endLabel = 'End',
  } = props;

  const range = isRange(mode);
  const withTime = hasTime(mode);
  const withDate = hasDate(mode);
  const isMobile = useIsMobile();

  const [open, setOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('start');
  const [draft, setDraft] = React.useState(() => (props.defaultOpen && !range ? parseWire(value, mode) : null));
  const [draftStart, setDraftStart] = React.useState(() => (props.defaultOpen && range ? parseWire(startValue, mode) : null));
  const [draftEnd, setDraftEnd] = React.useState(() => (props.defaultOpen && range ? parseWire(endValue, mode) : null));
  const [month, setMonth] = React.useState(() => {
    const seed = range ? parseWire(startValue, mode) : parseWire(value, mode);
    return seed || new Date();
  });

  React.useEffect(() => {
    if (props.defaultOpen) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWith = (tab) => {
    if (disabled) return;
    if (range) {
      const s = parseWire(startValue, mode);
      const e = parseWire(endValue, mode);
      setDraftStart(s); setDraftEnd(e); setActiveTab(tab);
      setMonth((tab === 'start' ? s : e) || s || e || new Date());
    } else {
      const d = parseWire(value, mode);
      setDraft(d); setMonth(d || new Date());
    }
    setOpen(true);
  };

  const activePart = range ? (activeTab === 'start' ? draftStart : draftEnd) : draft;
  const setActivePart = (d) => {
    if (range) { if (activeTab === 'start') setDraftStart(d); else setDraftEnd(d); }
    else setDraft(d);
  };

  const onCalSelect = (day) => {
    const merged = withTime ? mergeDateAndTime(day, activePart)
      : set(day, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
    setActivePart(merged);
    setMonth(day);
  };
  const onTimeChange = (time) => {
    const merged = withDate ? mergeDateAndTime(activePart || time, time)
      : set(new Date(), { hours: time.getHours(), minutes: time.getMinutes(), seconds: 0, milliseconds: 0 });
    setActivePart(merged);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    const p = tab === 'start' ? draftStart : draftEnd;
    if (p) setMonth(p);
  };

  const rangeInvalid = range && draftStart && draftEnd && !(draftStart < draftEnd);
  const incompleteRequired = range ? (required && (!draftStart || !draftEnd)) : (required && !draft);
  const [timeTextInvalid, setTimeTextInvalid] = React.useState(false);   // BS3-006: typed time not readable yet
  React.useEffect(() => { if (!open) setTimeTextInvalid(false); }, [open]);
  const applyDisabled = !!rangeInvalid || !!incompleteRequired || timeTextInvalid;
  const errorMsg = rangeInvalid ? 'End must be later than Start.' : (error || '');

  const commit = () => {
    if (range) onChange && onChange({ start: formatWire(draftStart, mode), end: formatWire(draftEnd, mode) });
    else onChange && onChange(formatWire(draft, mode));
    setOpen(false);
  };
  const cancel = () => setOpen(false);
  const clear = (e) => {
    e.stopPropagation();
    if (range) onChange && onChange({ start: '', end: '' });
    else onChange && onChange('');
  };

  const singleDisplay = formatDisplay(parseWire(value, mode), mode);
  const startDisplay = formatDisplay(parseWire(startValue, mode), mode);
  const endDisplay = formatDisplay(parseWire(endValue, mode), mode);

  const summaryCard = (
    <div className="mt-2 rounded-md border bg-muted/30 p-3 text-sm" data-testid={`${testId}-summary`}>
      {range ? (
        <div className="space-y-1">
          <div><span className="text-muted-foreground">Start: </span>{formatSummary(draftStart, mode) || '—'}</div>
          <div><span className="text-muted-foreground">End: </span>{formatSummary(draftEnd, mode) || '—'}</div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-violet-600 shrink-0" />
          {formatSummary(draft, mode) || '—'}
        </div>
      )}
    </div>
  );

  const renderBody = (scrollable) => (
    <div className={cn('tdt-body w-full', scrollable && 'flex flex-col min-h-0 flex-1 overflow-hidden')} data-testid={`${testId}-body`}>
      <div className={cn('pb-3 border-b', scrollable && 'shrink-0')}>
        <div className="text-base max-sm:text-lg font-semibold px-4 pt-2">
          Select date{withTime ? ' and time' : ''}
        </div>
        {range && (
          <div className="flex gap-4 px-4 mt-2">
            {['start', 'end'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => switchTab(t)}
                data-testid={`${testId}-tab-${t}`}
                className={cn(
                  'pb-2 text-sm font-medium border-b-2 -mb-[1px] transition-colors',
                  activeTab === t ? 'text-violet-700 border-violet-700' : 'text-muted-foreground border-transparent hover:text-foreground'
                )}
              >
                {t === 'start' ? startLabel : endLabel}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={cn('flex gap-4 p-3', withDate && withTime ? 'flex-col sm:flex-row' : 'flex-col', scrollable && 'min-h-0 flex-1 overflow-y-auto')}>
        {withDate && (
          <div className={cn('min-w-0', withTime ? 'sm:border-r sm:pr-4' : '')}>
            <CalendarPanel
              selected={activePart}
              onSelect={onCalSelect}
              month={month}
              onMonthChange={setMonth}
              minDate={minDate}
              maxDate={maxDate}
              disabledDates={disabledDates}
              rangeStart={range ? draftStart : undefined}
              rangeEnd={range ? draftEnd : undefined}
              testIdPrefix={testId}
            />
          </div>
        )}
        {withTime && (
          <div className="min-w-0 flex-1 sm:min-w-[240px]">
            <TimePanel date={activePart} onChange={onTimeChange} minuteStep={minuteStep} testIdPrefix={testId} onTextInvalid={setTimeTextInvalid} />
            {summaryCard}
          </div>
        )}
        {!withTime && withDate && summaryCard}
      </div>

      {errorMsg && (
        <div className={cn('px-4 pb-1 text-sm text-red-600', scrollable && 'shrink-0')} data-testid={`${testId}-error`} role="alert">{errorMsg}</div>
      )}

      <div className={cn('flex justify-end gap-3 p-4 border-t bg-background', scrollable && 'shrink-0')}>
        <Button type="button" variant="outline" onClick={cancel} className="max-sm:h-11 max-sm:px-5" data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button
          type="button"
          onClick={commit}
          disabled={applyDisabled}
          className="bg-violet-600 hover:bg-violet-700 text-white max-sm:h-11 max-sm:px-5"
          data-testid={`${testId}-apply`}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  const renderTrigger = ({ fieldLabel, display, onClick, tid }) => (
    <div className="w-full">
      {fieldLabel && <div className="text-sm mb-1 font-medium" data-testid={`${tid}-label`}>{fieldLabel}{required && <span className="text-red-500 ml-0.5">*</span>}</div>}
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        id={id}
        data-testid={tid}
        aria-haspopup="dialog"
        aria-expanded={open || undefined}
        aria-invalid={(!!error || !!rangeInvalid) || undefined}
        aria-describedby={errorMsg ? `${testId}-error` : undefined}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-red-500',
          className
        )}
      >
        <span className={cn(!display && 'text-muted-foreground')}>
          {display || placeholder || placeholderFor(mode)}
        </span>
        <span className="flex items-center gap-1">
          {allowClear && display && (
            <span role="button" tabIndex={-1} aria-label="Clear" onClick={clear} data-testid={`${tid}-clear`}>
              <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
            </span>
          )}
          {withDate
            ? <CalendarIcon className="w-4 h-4 text-muted-foreground" />
            : <Clock className="w-4 h-4 text-muted-foreground" />}
        </span>
      </button>
    </div>
  );

  const triggers = range ? (
    <div className="flex flex-col sm:flex-row gap-3 w-full">
      {renderTrigger({ fieldLabel: startLabel, display: startDisplay, onClick: () => openWith('start'), tid: `${testId}-trigger-start` })}
      {renderTrigger({ fieldLabel: endLabel, display: endDisplay, onClick: () => openWith('end'), tid: `${testId}-trigger-end` })}
    </div>
  ) : (
    <div className="w-full">
      {renderTrigger({ fieldLabel: label, display: singleDisplay, onClick: () => openWith('start'), tid: testId })}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {triggers}
        <Sheet open={open} onOpenChange={(o) => !o && setOpen(false)}>
          <SheetContent side="bottom" className="p-0 max-h-[92vh] overflow-y-auto rounded-t-2xl" data-testid={`${testId}-sheet`}>
            {renderBody(false)}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => !o && setOpen(false)}>
      <PopoverAnchor asChild>{triggers}</PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        avoidCollisions
        collisionPadding={12}
        sticky="always"
        className="w-auto p-0 flex flex-col overflow-hidden max-w-[calc(100vw-24px)] sm:max-w-[560px]"
        style={{ maxHeight: 'min(85dvh, var(--radix-popover-content-available-height))' }}
        data-testid={`${testId}-popover`}
      >
        {renderBody(true)}
      </PopoverContent>
    </Popover>
  );
}
